import { useState } from "react";
import Icon from "../Icon";
import { MaterialNames } from "../material_symbol_names";

const sharedClassName = "object-cover rounded-full";

const sizes = {
  small: { className: "w-6 h-6", size: 16 },
  medium: { className: "w-12 h-12", size: 28 },
  large: { className: "w-[70px] h-[70px]", size: 40 },
  xlarge: { className: "w-[110px] h-[110px]", size: 60 }
};

interface AvatarPlaceholderProps {
  size: keyof typeof sizes;
  className?: string;
  iconClassName?: string;
  iconName?: MaterialNames;
  decorative?: boolean;
}

function AvatarPlaceholder({
  size,
  className,
  iconClassName,
  iconName = "person",
  decorative = false
}: AvatarPlaceholderProps) {
  const sizeData = sizes[size];

  return (
    <div
      {...(decorative ? { "aria-hidden": true as const } : { role: "img" as const, "aria-label": "Profile photo" })}
      className={`${sharedClassName} ${sizeData.className} bg-sys-brand-secondary-container flex items-center justify-center ${className ?? ""}`}
      data-testid="avatar-person-icon"
    >
      <Icon name={iconName} size={sizeData.size} className={iconClassName} />
    </div>
  );
}

interface Props {
  picture?: string;
  size?: keyof typeof sizes;
  className?: string;
  iconClassName?: string;
  iconName?: MaterialNames;
  onPictureClick?: () => void;
}

export default function Avatar({
  picture,
  size = "medium",
  className,
  iconClassName,
  iconName = "person",
  onPictureClick
}: Props) {
  const [failedPicture, setFailedPicture] = useState<string | undefined>();

  const sizeData = sizes[size];
  const showImage = Boolean(picture) && picture !== failedPicture;
  const pictureClickLabel = picture ? "View profile photo" : "Add profile photo";
  const pictureButtonClassName = `group relative block cursor-pointer rounded-full border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary ${sizeData.className} ${className ?? ""}`;

  if (onPictureClick) {
    return (
      <button
        type="button"
        className={pictureButtonClassName}
        onClick={onPictureClick}
        aria-label={pictureClickLabel}
        data-testid="avatar-picture-button"
      >
        {showImage ? (
          <img
            src={picture}
            className={`${sharedClassName} ${sizeData.className}`}
            alt=""
            data-testid="avatar-image"
            onError={() => setFailedPicture(picture)}
          />
        ) : (
          <AvatarPlaceholder size={size} iconClassName={iconClassName} iconName={iconName} decorative />
        )}
        {picture ? null : (
          <span
            className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-sys-brand-scrim/50 opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
            data-testid="avatar-add-photo-overlay"
            aria-hidden="true"
          >
            <Icon name="photo_camera" size={Math.round(sizeData.size * 0.6)} className="text-sys-brand-on-primary" />
          </span>
        )}
      </button>
    );
  }

  if (showImage) {
    return (
      <img
        src={picture}
        className={`${sharedClassName} ${sizeData.className} ${className}`}
        alt="Rounded avatar"
        data-testid="avatar-image"
        onError={() => setFailedPicture(picture)}
      />
    );
  }

  return <AvatarPlaceholder size={size} className={className} iconClassName={iconClassName} iconName={iconName} />;
}

interface EditableAvatarProps {
  picture?: string;
  size?: keyof typeof sizes;
  onEditClick?: () => void;
  className?: string;
  iconName?: MaterialNames;
  editIconName?: MaterialNames;
}

export function EditableAvatar({
  picture,
  size = "medium",
  onEditClick,
  className,
  iconName = "person",
  editIconName = "photo_camera"
}: EditableAvatarProps) {
  const [failedPicture, setFailedPicture] = useState<string | undefined>();

  const sizeData = sizes[size];
  const showImage = Boolean(picture) && picture !== failedPicture;

  return (
    <div className={`relative group ${sizeData.className} ${className}`}>
      {showImage ? (
        <img
          src={picture}
          className={`${sharedClassName} ${sizeData.className}`}
          alt="Rounded avatar"
          data-testid="avatar-image"
          onError={() => setFailedPicture(picture)}
        />
      ) : (
        <AvatarPlaceholder size={size} iconName={iconName} iconClassName="text-sys-brand-on-surface-variant" />
      )}

      {onEditClick && (
        <div
          className="absolute inset-0 bg-sys-brand-scrim/50 rounded-full !self-auto flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
          onClick={onEditClick}
          data-testid="avatar-edit-overlay"
        >
          <Icon name={editIconName} size={sizeData.size * 0.6} className="text-sys-brand-on-primary" />
        </div>
      )}
    </div>
  );
}
